import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { EditIcon, Delete02Icon, PlusIcon } from '../../../../../assets/icons';
import ProductModal from '../../../../../components/modals/UserProfile/ProductModal';
import DeletePopup from '../../../../../components/delete-popup';
import { addUserProduct, updateUserProduct, deleteUserProduct } from '../../../../../redux/slice/allUsersSlice';
import PreviewModal from '../../../../../components/modals/previewModal';
import { useToast } from '../../../../../context/TostContext';
import { IconBtn } from '../../../../../components/commonButton/btns';

const ImageSwiper = ({ media, title, onImageClick }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        setCurrentIndex(0);
    }, [media]);

    if (!media || media.length === 0) return null;

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
    };

    const currentMedia = media[currentIndex] || media[0] || {};

    return (
        <div className="position-relative rounded-3 overflow-hidden border shadow-sm" style={{ width: "100px", height: "100px", flexShrink: 0, backgroundColor: '#f8f9fa' }}>
            {currentMedia?.mimetype === "video/mp4" ?
                <video src={currentMedia.file?.url || currentMedia.url}
                    alt={title || "Product Media"}
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }} /> :
                <img
                    src={currentMedia.file?.url || currentMedia.url}
                    alt={title || "Product Media"}
                    className="w-100 h-100"
                    style={{ objectFit: "cover", cursor: "pointer" }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onImageClick) onImageClick(currentIndex);
                    }}
                    onError={(e) => { e.target.src = '/images/image-placeholder.png'; }}
                />}

            {media.length > 1 && (
                <>
                    <button
                        onClick={handlePrev}
                        className="position-absolute top-50 start-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
                        style={{ width: "20px", height: "20px", borderRadius: "0 4px 4px 0", cursor: "pointer", fontSize: "12px" }}
                    >
                        &#10094;
                    </button>
                    <button
                        onClick={handleNext}
                        className="position-absolute top-50 end-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
                        style={{ width: "20px", height: "20px", borderRadius: "4px 0 0 4px", cursor: "pointer", fontSize: "12px" }}
                    >
                        &#10095;
                    </button>
                    <div className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-1 mb-1">
                        {media.map((_, idx) => (
                            <div
                                key={idx}
                                className="rounded-circle"
                                style={{
                                    width: "4px",
                                    height: "4px",
                                    backgroundColor: currentIndex === idx ? "white" : "rgba(255,255,255,0.5)"
                                }}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const Products = ({ userDetail }) => {
    const {id} = useParams();
    const dispatch = useDispatch();
    const toast = useToast();
    const [showModal, setShowModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewMediaArray, setPreviewMediaArray] = useState([]);
    const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

    const { productLoading } = useSelector((state) => state.allUsers);

    const handleUpsertProduct = (data) => {
        const action = currentItem
            ? updateUserProduct({ id: id, productId: currentItem.id, data })
            : addUserProduct({ id: id, data });

        dispatch(action)
            .unwrap()
            .then(() => {
                toast.success(`Product ${currentItem ? "updated" : "added"} successfully`);
                setShowModal(false);
                setCurrentItem(null);
            })
            .catch((error) => {
                toast.error(error?.message || `Failed to ${currentItem ? "update" : "add"} product`);
            });
    };

    const handleDeleteProduct = () => {
        dispatch(deleteUserProduct({ id: id, productId: itemToDelete }))
            .unwrap()
            .then(() => {
                toast.success("Product deleted successfully");
                setShowDeletePopup(false);
                setItemToDelete(null);
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to delete product");
            });
    };

    return (
        <div className="position-relative h-100">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="fw-bold mb-0" style={{ fontSize: '18px' }}>Products & Services</h6>
                <IconBtn size={32} icon={<PlusIcon size={16} />} className='rounded-circle' onClick={() => {
                    setCurrentItem(null);
                    setShowModal(true);
                }} />

            </div>
            {userDetail?.products?.length > 0 ? (
                <div className="d-flex flex-column gap-4">
                    {userDetail.products.map((prod, index) => (
                        <div key={index} className="p-3 border rounded-3 bg-light-subtle position-relative custom-card">
                            <div className="position-absolute top-0 end-0 p-2 d-flex gap-2">
                                <button
                                    className="post-action-btn edit-btn border bg-white"
                                    onClick={() => {
                                        setCurrentItem(prod);
                                        setShowModal(true);
                                    }}
                                >
                                    <EditIcon width={14} height={14} color="#007bff" />
                                </button>
                                <button
                                    className="post-action-btn delete-btn border bg-white"
                                    onClick={() => {
                                        setItemToDelete(prod.id);
                                        setShowDeletePopup(true);
                                    }}
                                >
                                    <Delete02Icon width={14} height={14} color="#FF0000" />
                                </button>
                            </div>
                            <div className="d-flex gap-4">
                                {prod.media?.length > 0 && (
                                    <ImageSwiper
                                        media={prod.media}
                                        title={prod.title}
                                        onImageClick={(index) => {
                                            const mediaArray = prod.media.map(item => ({
                                                url: item.file?.url || item.url,
                                                title: prod.title || "Product Image",
                                                type: "image"
                                            }));
                                            setPreviewMediaArray(mediaArray);
                                            setPreviewInitialIndex(index);
                                            setShowPreviewModal(true);
                                        }}
                                    />
                                )}
                                <div className="flex-grow-1">
                                    <h6 className="fw-bold text-dark fs-6 mb-1">{prod.title || "-"}</h6>
                                    {prod.description && (
                                        <p className="text-secondary text-break mb-0 mt-2" style={{ lineHeight: '1.6', fontSize: '14px' }}>{prod.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-5 text-muted">
                    <p className="mb-0">No products listed.</p>
                </div>
            )}

            <ProductModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setCurrentItem(null);
                }}
                onSubmit={handleUpsertProduct}
                product={currentItem}
                isLoading={productLoading}
                title={currentItem ? "Edit Product" : "Add Product"}
                size="lg"
            />

            <DeletePopup
                show={showDeletePopup}
                onHide={() => {
                    setShowDeletePopup(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDeleteProduct}
                content="Are you sure you want to delete this product?"
                saveTitle="Delete"
                disabled={productLoading}
            />

            <PreviewModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                previewMedia={previewMediaArray}
                initialIndex={previewInitialIndex}
            />
        </div>
    );
};

export default Products;
